/**
 * Order status timeline — single source for the lifecycle states an
 * order moves through (forward fulfilment + return branches). Backed
 * by the `order_status_history` table (migration 0120) — every
 * transition appends a row, and the "current" status is always the
 * latest row's value. Kiran 2026-06-17.
 *
 * The status enum is locked to the CHECK constraint on the migration.
 * Any change here REQUIRES a follow-up migration that loosens the
 * CHECK first.
 */

export const ORDERSTATUS = {
  CONFIRMED: 'confirmed',
  PACKED: 'packed',
  SHIPPED: 'shipped',
  INTRANSIT: 'in_transit',
  OUTFORDELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  // Forward terminal — auto-stamped by the cron RETURNWINDOWDAYS days
  // after `delivered` if no return was filed.
  ACCEPTED: 'accepted',
  // Customer cancels a still-unshipped order (Kiran 2026-06-19) within
  // CANCEL.WINDOWDAYS. Terminal — separate from REJECTED (which is the
  // operator rejecting a return request).
  CANCELLED: 'cancelled',
  // Return branch — opened by the customer's Return button on a
  // delivered order.
  RETURNPENDING: 'return_pending',
  RETURNCONFIRMED: 'return_confirmed',
  RETURNPICKUP: 'return_pickup',
  RETURNRECEIVED: 'return_received',
  REFUNDPROCESSED: 'refund_processed',
  REFUNDED: 'refunded',
  // Return terminal (rejected) — operator rejects the request.
  REJECTED: 'rejected'
} as const

export type OrderStatus = (typeof ORDERSTATUS)[keyof typeof ORDERSTATUS]

/** Human-readable label per status — shown on the customer timeline +
 *  the operator transition buttons. */
export const ORDERSTATUSLABEL: Record<OrderStatus, string> = {
  [ORDERSTATUS.CONFIRMED]: 'Order placed',
  [ORDERSTATUS.PACKED]: 'Packed',
  [ORDERSTATUS.SHIPPED]: 'Shipped',
  [ORDERSTATUS.INTRANSIT]: 'In transit',
  [ORDERSTATUS.OUTFORDELIVERY]: 'Out for delivery',
  [ORDERSTATUS.DELIVERED]: 'Delivered',
  [ORDERSTATUS.ACCEPTED]: 'Accepted',
  [ORDERSTATUS.CANCELLED]: 'Cancelled',
  [ORDERSTATUS.RETURNPENDING]: 'Return requested',
  [ORDERSTATUS.RETURNCONFIRMED]: 'Return confirmed',
  [ORDERSTATUS.RETURNPICKUP]: 'Pickup scheduled',
  [ORDERSTATUS.RETURNRECEIVED]: 'Return received',
  [ORDERSTATUS.REFUNDPROCESSED]: 'Refund processing',
  [ORDERSTATUS.REFUNDED]: 'Refunded',
  [ORDERSTATUS.REJECTED]: 'Return rejected'
}

/**
 * Allowed next-status set per current status. The handler validates
 * any operator/customer-driven transition against this map and 409s
 * an illegal jump. Cron auto-flips (delivered → accepted) bypass via
 * a separate helper; everything else honours the graph.
 *
 * Empty array = terminal state.
 */
/** Pre-shipping states — the customer can CANCEL the order from these
 *  while still within CANCEL.WINDOWDAYS (Kiran 2026-06-19). Once
 *  shipping starts the cancel option goes away and the return path
 *  takes over (delivered-only). */
const CANCELLABLE_STATUSES: ReadonlyArray<OrderStatus> = [
  ORDERSTATUS.CONFIRMED,
  ORDERSTATUS.PACKED
]
const CANCELLABLE_SET = new Set<OrderStatus>(CANCELLABLE_STATUSES)

export const ORDERSTATUSTRANSITIONS: Record<OrderStatus, ReadonlyArray<OrderStatus>> = {
  // Forward fulfilment — operator advances each step. The pre-shipping
  // steps can also branch to CANCELLED (customer Cancel-order).
  [ORDERSTATUS.CONFIRMED]: [ORDERSTATUS.PACKED, ORDERSTATUS.CANCELLED],
  [ORDERSTATUS.PACKED]: [ORDERSTATUS.SHIPPED, ORDERSTATUS.CANCELLED],
  [ORDERSTATUS.SHIPPED]: [ORDERSTATUS.INTRANSIT],
  [ORDERSTATUS.INTRANSIT]: [ORDERSTATUS.OUTFORDELIVERY],
  [ORDERSTATUS.OUTFORDELIVERY]: [ORDERSTATUS.DELIVERED],
  [ORDERSTATUS.DELIVERED]: [ORDERSTATUS.RETURNPENDING, ORDERSTATUS.ACCEPTED],
  [ORDERSTATUS.ACCEPTED]: [],
  [ORDERSTATUS.CANCELLED]: [],
  // Customer can cancel a still-pending return; reverts to 'delivered'
  // (Return is only filed from 'delivered' now, so that's the prior
  // state by construction). Operator paths (RETURNCONFIRMED + REJECTED)
  // sit alongside.
  [ORDERSTATUS.RETURNPENDING]: [
    ORDERSTATUS.RETURNCONFIRMED,
    ORDERSTATUS.REJECTED,
    ORDERSTATUS.DELIVERED
  ],
  [ORDERSTATUS.RETURNCONFIRMED]: [ORDERSTATUS.RETURNPICKUP],
  [ORDERSTATUS.RETURNPICKUP]: [ORDERSTATUS.RETURNRECEIVED],
  [ORDERSTATUS.RETURNRECEIVED]: [ORDERSTATUS.REFUNDPROCESSED],
  [ORDERSTATUS.REFUNDPROCESSED]: [ORDERSTATUS.REFUNDED],
  [ORDERSTATUS.REFUNDED]: [],
  [ORDERSTATUS.REJECTED]: []
}

/** A status falls on the RETURN branch (vs forward fulfilment). Used
 *  for badge tone + the customer "Return" button gating. */
export function isReturnBranch(status: OrderStatus): boolean {
  return (
    status === ORDERSTATUS.RETURNPENDING ||
    status === ORDERSTATUS.RETURNCONFIRMED ||
    status === ORDERSTATUS.RETURNPICKUP ||
    status === ORDERSTATUS.RETURNRECEIVED ||
    status === ORDERSTATUS.REFUNDPROCESSED ||
    status === ORDERSTATUS.REFUNDED ||
    status === ORDERSTATUS.REJECTED
  )
}

/** Whether the customer can click "Return" given the current status.
 *  Kiran 2026-06-19: only available from `delivered` ("after it is
 *  received, only then the order can be returned"). Operator-side
 *  intermediate steps stay invisible to the customer Return flow. */
export function isReturnable(currentStatus: OrderStatus): boolean {
  return currentStatus === ORDERSTATUS.DELIVERED
}

/** Whether the customer can click "Cancel order" given the current
 *  status + placed-at + the configured cancel window.
 *  Kiran 2026-06-19: cancel is allowed while the order is still
 *  `confirmed` or `packed` AND within CANCEL.WINDOWDAYS of placed_at.
 *  Once shipping starts, the cancel button hides. */
export function isCancellable(
  currentStatus: OrderStatus,
  placedAt: string | null,
  windowDays: number,
  now: Date
): boolean {
  if (!CANCELLABLE_SET.has(currentStatus)) return false
  if (placedAt === null) return false
  const placedTime = Date.parse(placedAt)
  if (!Number.isFinite(placedTime)) return false
  const windowMs = windowDays * 24 * 60 * 60 * 1000
  return now.getTime() - placedTime <= windowMs
}
